import { useMutation, useQuery } from '@tanstack/react-query';

import { getList, getStatusOfProcessing, updateList } from '../../../services/processing';
import {
  confirmInfo,
  getDevelopments,
  getPrecincts,
  getZones,
  extractionDocuments,
  getExtractionData,
} from '../../../services/extraction';
import { analyseData, getAnalysisData } from '../../../services/analysis';

const isCompleteMsg = (detail = '') => detail.toLowerCase().includes('complete');

export const useGetList = (listId) =>
  useQuery({
    queryKey: ['applicationList', listId],
    queryFn: () => getList(listId),
    enabled: !!listId,
  });

export const useGetStatus = (listId, options = {}) =>
  useQuery({
    queryKey: ['processingStatus', listId],
    queryFn: () => getStatusOfProcessing(listId),
    enabled: !!listId,
    ...options,
  });

export const useUpdateList = (options = {}) =>
  useMutation({ mutationFn: (payload) => updateList(payload), ...options });

/**
 * Trigger extraction, then fetch data once the trigger reports "Extraction
 * Complete!". Call `trigger()` to (re)start / "check again".
 */
export const useExtraction = (listId) => {
  const extractMutation = useMutation({ mutationFn: () => extractionDocuments(listId) });

  const isComplete = isCompleteMsg(
    extractMutation.data?.detail ?? extractMutation.data?.message ?? ''
  );

  const extractionQuery = useQuery({
    queryKey: ['extractionData', listId],
    queryFn: () => getExtractionData(listId),
    enabled: !!listId && isComplete,
  });

  return { extractMutation, extractionQuery, isComplete, trigger: () => extractMutation.mutate() };
};

/**
 * Same pattern as useExtraction, for analysis.
 */
export const useAnalysis = (listId) => {
  const analyseMutation = useMutation({ mutationFn: () => analyseData(listId) });

  const isComplete = isCompleteMsg(
    analyseMutation.data?.detail ?? analyseMutation.data?.message ?? ''
  );

  const analysisQuery = useQuery({
    queryKey: ['analysisData', listId],
    queryFn: () => getAnalysisData(listId),
    enabled: !!listId && isComplete,
  });

  return { analyseMutation, analysisQuery, isComplete, trigger: () => analyseMutation.mutate() };
};

export const useZones = () =>
  useQuery({ queryKey: ['zones'], queryFn: getZones, staleTime: 10 * 60 * 1000 });

export const usePrecincts = (zoneId) =>
  useQuery({
    queryKey: ['precincts', zoneId],
    queryFn: () => getPrecincts(zoneId),
    enabled: !!zoneId, // "not allowed to choose until zone select"
    staleTime: 10 * 60 * 1000,
  });

export const useDevelopments = () =>
  useQuery({ queryKey: ['developments'], queryFn: getDevelopments, staleTime: 10 * 60 * 1000 });

export const useConfirmInfo = (options = {}) =>
  useMutation({ mutationFn: (payload) => confirmInfo(payload), ...options });
